import React from 'react';
import {useComponent} from '../component-library';
import {useFeatureAppId} from '../utils/use-feature-app-id';
import {generateTestId, TestIdElementType} from '../utils/test';
import {ConfigureButtonConfiguration} from '../types';
import styled from 'styled-components';
import {ConfigureButtonWrapper} from './configure-button-wrapper';
import {HeaderIcon, HeaderIconProps} from './header-icon';
import {useGetAppDataContentFlag} from '../utils/use-get-app-data-content-flag';
import {AccessibleLabel} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

interface HeaderProps extends HeaderIconProps {
  heading: string;
  showConfigureButton?: boolean;
  configureButtonConfiguration?: ConfigureButtonConfiguration;
  configureButtonTranslations?: AccessibleLabel;
}

export function Header({
  heading,
  showConfigureButton,
  configureButtonTranslations,
  configureButtonConfiguration,
  featureApp,
}: HeaderProps): JSX.Element {
  const Text = useComponent('Text');
  const ConfigureButton = useComponent('ConfigureButton');
  const featureAppId = useFeatureAppId();

  const appDataContentHideConfigureButton = useGetAppDataContentFlag(
    'hideConfigureButton'
  );
  const displayConfigureButton =
    !appDataContentHideConfigureButton &&
    !!(showConfigureButton && ConfigureButton);

  return (
    <HeaderWrapper>
      <HeaderWithIconWrapper>
        <HeaderIconWrapper>
          <HeaderIcon featureApp={featureApp} />
        </HeaderIconWrapper>
        <HeaderTextWrapper>
          <Text
            tag="h2"
            textAlign={'start'}
            variant="headline"
            testid={generateTestId({
              featureAppId,
              elementName: 'header-title',
              elementType: TestIdElementType.static,
            })}
          >
            {heading}
          </Text>
        </HeaderTextWrapper>
      </HeaderWithIconWrapper>
      <ConfigureButtonContainer>
        <ConfigureButtonWrapper
          showButton={displayConfigureButton}
          configuration={configureButtonConfiguration}
          translations={configureButtonTranslations}
        />
      </ConfigureButtonContainer>
    </HeaderWrapper>
  );
}

const HeaderWithIconWrapper = styled.div`
  ${(props) =>
    `
        display: grid;
        align-items: 'start';
        gap: ${props.theme.internal.size.static300};
        grid-template-areas: 'icon header';
        grid-template-columns: 'auto 1fr'
      `}
`;

const HeaderIconWrapper = styled.div`
  grid-area: icon;
`;

const HeaderTextWrapper = styled.div`
  grid-area: header;
  align-content: center;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: ${(props) => props.theme.internal.size.static300};
`;

const ConfigureButtonContainer = styled.div`
  display: none;
  flex: none;

  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b960}px) {
    display: block;
  }
`;
